import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Camera, Sparkles, Upload, IndianRupee, Trophy, CheckCircle2, Clock, Image as ImageIcon, X, MapPin } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { photoUrl } from "../lib/photos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useServices() {
  const [services, setServices] = useState([]);
  useEffect(() => { fetch(`${API}/services`).then((r) => r.json()).then((d) => setServices(d.services || [])); }, []);
  return services;
}

function StatusChip({ status }) {
  const map = {
    approved: { c: "text-emerald-700 border-emerald-200 bg-emerald-50", i: CheckCircle2, l: "approved" },
    pending: { c: "text-amber-700 border-amber-200 bg-amber-50", i: Clock, l: "in review" },
    rejected: { c: "text-red-700 border-red-200 bg-red-50", i: X, l: "rejected" },
  };
  const s = map[status] || map.pending;
  const I = s.i;
  return <span className={`chip ${s.c}`}><I className="w-3 h-3" /> {s.l}</span>;
}

export default function EarnByHelping() {
  const { user, token, authFetch } = useAuth();
  const services = useServices();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [serviceSlug, setServiceSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [feed, setFeed] = useState([]);
  const [totalCredits, setTotalCredits] = useState(0);

  const loadFeed = () => {
    fetch(`${API}/community/photos?status_filter=approved&limit=48`).then((r) => r.json())
      .then((d) => setFeed(d.photos || []));
  };
  const loadMine = () => {
    if (!token) return;
    authFetch({ url: `${API}/community/photos/mine` })
      .then((r) => { setMine(r.data.photos || []); setTotalCredits(r.data.total_credits_inr || 0); })
      .catch(() => {});
  };

  useEffect(() => { loadFeed(); loadMine(); /* eslint-disable-next-line */ }, [token]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!token) { toast.info("Sign in first to earn credits"); nav("/login?next=/earn-by-helping"); return; }
    if (!file) { toast.error("Please pick a photo"); return; }
    if (caption.trim().length < 3) { toast.error("Add a short caption (3–280 chars)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const upl = await authFetch({
        method: "POST", url: `${API}/upload/community-photo`, data: fd,
        headers: { "Content-Type": "multipart/form-data" },
      });
      await authFetch({
        method: "POST", url: `${API}/community/photos`,
        data: { storage_path: upl.data.path, caption, service_slug: serviceSlug || null },
      });
      toast.success("Photo submitted — earn ₹25 credits on approval");
      setFile(null); setPreview(null); setCaption(""); setServiceSlug("");
      loadMine(); loadFeed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-24">
      {/* HERO */}
      <div className="grid lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-7">
          <div className="chip" style={{ borderColor: "var(--secondary)", color: "var(--secondary)" }}>
            <Sparkles className="w-3.5 h-3.5" /> Earn just by Helping
          </div>
          <h1 className="mt-4 font-display text-5xl md:text-7xl font-bold leading-[0.98]">
            Real photos. <span className="italic" style={{ color: "var(--secondary)" }}>Real experience.</span>
          </h1>
          <p className="mt-5 text-lg text-[color:var(--text-muted)] max-w-xl leading-relaxed">
            Share a real photo of a HeylpMate task you helped with — a Mate carrying luggage, an elder being escorted,
            a happy customer. Get <b>₹25 credits</b> per approved photo. Use credits toward your next booking.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <span className="chip"><IndianRupee className="w-3.5 h-3.5" /> ₹25 per approved photo</span>
            <span className="chip"><Trophy className="w-3.5 h-3.5" /> Top posts get featured</span>
            <span className="chip"><CheckCircle2 className="w-3.5 h-3.5" /> Minimal credits — real proof</span>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-3xl border-2 border-black/10 bg-white p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow">your credits</div>
                <div className="font-display text-4xl mt-1" style={{ color: "var(--secondary)" }}>₹{totalCredits}</div>
              </div>
              <IndianRupee className="w-10 h-10 text-orange-200" />
            </div>
            <div className="mt-4 text-xs text-neutral-500">
              {mine.length} photo{mine.length === 1 ? "" : "s"} submitted · redeem on any booking.
            </div>
            {!token && (
              <Link to="/login?next=/earn-by-helping" className="btn-primary w-full mt-4 text-center block">Sign in to earn</Link>
            )}
          </div>
        </div>
      </div>

      {/* UPLOADER */}
      <section className="mt-12 rounded-3xl border-2 border-black/10 bg-white p-6 md:p-8">
        <div className="grid md:grid-cols-2 gap-8 items-start">
          <div>
            <span className="label-eyebrow">upload a real photo</span>
            <h2 className="font-display text-3xl font-bold mt-3">Turn every helpful moment into ₹25.</h2>
            <p className="mt-2 text-sm text-neutral-600">Please only submit photos with the subject's consent. No selfies, no personal IDs.</p>
            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Photo</Label>
                <div className="mt-1.5">
                  <input ref={fileRef} data-testid="community-photo-input" type="file" accept="image/*" onChange={onPickFile} className="hidden" />
                  <button type="button" onClick={() => fileRef.current?.click()} data-testid="pick-photo-btn"
                    className="w-full border-2 border-dashed border-black/15 rounded-2xl p-6 text-center hover:border-[color:var(--secondary)] transition-colors">
                    {preview ? (
                      <div className="flex items-center gap-3">
                        <img src={preview} alt="preview" className="w-16 h-16 object-cover rounded-xl" />
                        <div className="text-sm">
                          <div className="font-bold">{file?.name}</div>
                          <div className="text-neutral-500 text-xs">Tap to change</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-neutral-500">
                        <Camera className="w-8 h-8 mx-auto text-neutral-400" />
                        <div className="mt-2 text-sm">Tap to pick a photo</div>
                        <div className="text-[11px] mt-1">JPG / PNG / WEBP up to 8MB</div>
                      </div>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Caption</Label>
                <Textarea data-testid="community-caption-input" rows={3} className="mt-1.5"
                  placeholder="What happened? e.g. 'Ravi carried 4 bags up 3 floors — mom was so grateful.'"
                  value={caption} onChange={(e) => setCaption(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500">Assist (optional)</Label>
                <select data-testid="community-service-select" value={serviceSlug} onChange={(e) => setServiceSlug(e.target.value)}
                  className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  <option value="">— pick a service —</option>
                  {services.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                </select>
              </div>
              <button data-testid="community-submit-btn" onClick={submit} disabled={busy}
                className="btn-primary w-full inline-flex items-center justify-center gap-2">
                <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Submit for +₹25 credits"}
              </button>
            </div>
          </div>

          <div>
            <span className="label-eyebrow">your submissions</span>
            <div className="mt-3 space-y-3 max-h-[520px] overflow-y-auto pr-1">
              {mine.length === 0 && <div className="text-sm text-neutral-500">No photos yet. Your first submission goes live for review here.</div>}
              {mine.map((p) => (
                <div key={p.id} className="flex gap-3 items-center rounded-2xl border border-black/10 bg-white p-3">
                  <img src={photoUrl(p.storage_path)} alt="" className="w-16 h-16 object-cover rounded-xl border border-black/5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{p.caption}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <StatusChip status={p.status} />
                      {p.status === "approved" && <span className="text-xs font-bold" style={{ color: "var(--secondary)" }}>+₹{p.credits_awarded}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PUBLIC FEED */}
      <section className="mt-12">
        <span className="label-eyebrow">real moments · community wall</span>
        <h2 className="font-display text-3xl md:text-4xl font-bold mt-3">See what real help looks like.</h2>
        {feed.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-black/10 bg-white p-10 text-center">
            <ImageIcon className="w-10 h-10 mx-auto text-neutral-300" />
            <div className="mt-3 text-sm text-neutral-500">No approved photos yet. Yours could be first.</div>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            {feed.map((p) => (
              <div key={p.id} data-testid={`feed-photo-${p.id}`} className="relative rounded-2xl overflow-hidden aspect-square border border-black/10 card-lift">
                <img src={photoUrl(p.storage_path)} alt={p.caption} className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 p-2 text-white bg-gradient-to-t from-black/70 to-transparent">
                  <div className="text-[11px] font-bold truncate">{p.user_name}</div>
                  <div className="text-[10px] opacity-90 line-clamp-2">{p.caption}</div>
                  {p.location_name && (
                    <div className="text-[9px] opacity-70 mt-0.5 flex items-center gap-0.5">
                      <MapPin className="w-2.5 h-2.5" /> {p.location_name}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
